// src/components/Logs/LogsViewer.jsx

import React, { useEffect, useMemo, useState, useCallback } from "react"
import { useSearchParams } from "react-router-dom"
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Chip,
    Collapse,
    Divider,
    Grid,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    MenuItem,
    Skeleton,
    Stack,
    Tooltip,
    Typography,
    Button,
    Link,
    TextField,
    InputAdornment,
} from "@mui/material"
import {
    Refresh,
    OpenInNew,
    ContentCopy,
    CheckCircleOutline,
    ErrorOutline,
    DescriptionOutlined,
    TerminalOutlined,
    Search as SearchIcon,
    Clear as ClearIcon,
    Cancel as CancelIcon,
    FilterList as FilterListIcon,
    NavigateBefore,
    NavigateNext,
} from "@mui/icons-material"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import moment from "moment"
import "moment/locale/pt-br"
import { api } from "../../services/Main/Api"

// -------- helpers --------
const safeStringify = (value) => {
    try {
        return JSON.stringify(value ?? {}, null, 2)
    } catch {
        return JSON.stringify({ error: "Falha ao serializar JSON" }, null, 2)
    }
}

const formatTime = (dateLike) => {
    if (!dateLike) return "-"
    const d = new Date(dateLike)
    if (Number.isNaN(d.getTime())) return "-"
    return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })
}

const formatDate = (dateLike) => {
    if (!dateLike) return "-"
    const d = new Date(dateLike)
    if (Number.isNaN(d.getTime())) return "-"
    return d.toLocaleDateString("pt-BR")
}

const methodColor = (m) => {
    const method = String(m || "").toUpperCase()
    if (method === "POST") return "primary"
    if (method === "DELETE") return "error"
    if (method === "PATCH" || method === "PUT") return "warning"
    if (method === "GET") return "info"
    return "default"
}

const copyText = async (txt) => {
    const text = String(txt ?? "")
    try {
        await navigator.clipboard.writeText(text)
    } catch {
        const textarea = document.createElement("textarea")
        textarea.value = text
        document.body.appendChild(textarea)
        textarea.select()
        document.execCommand("copy")
        document.body.removeChild(textarea)
    }
}

const cleanParams = (obj) => {
    const out = {}
    Object.entries(obj || {}).forEach(([k, v]) => {
        if (v === undefined || v === null) return
        const s = String(v).trim()
        if (!s) return
        out[k] = s
    })
    return out
}

// debounce simples (sem libs)
const useDebouncedValue = (value, delay = 450) => {
    const [debounced, setDebounced] = useState(value)
    useEffect(() => {
        const t = setTimeout(() => setDebounced(value), delay)
        return () => clearTimeout(t)
    }, [value, delay])
    return debounced
}

// rótulos bonitos pros chips de GET
const FILTER_LABELS = {
    invoice_id: "Fatura",
    signed_recipient_id: "Recipient",
    condo_id: "Condo",
    transfer_id: "Repasse",
    payment_id: "Pagamento",
    identifier: "Identificador",
    method: "Método",
    id: "IDs",
    start_date: "Data inicial",
    end_date: "Data final",
}

// métodos disponíveis para o select
const METHOD_OPTIONS = [
    "Boleto",
    "GeneratePixQrCodeDueDate",
    "GenerateBoleto",
    "InternalTransfer",
    "CancelBoleto",
    "GeneratePixOut",
    "PixOut",
    "CancelQRCodePix",
    "MoneyTransfer",
    "PixIn",
    "GetInfosByBarcode",
    "BoletoOut",
    "EntryChange",
    "GetInfosCIPByBarcode",
    "GenerateBoletoOut",
    "GetAccountOperationLimit",
]

// tokens suportados no search => parâmetro na URL
const TOKEN_TO_QUERY_PARAM = {
    invoice: "invoice_id",
    invoice_id: "invoice_id",
    condo: "condo_id",
    condo_id: "condo_id",
    recipient: "signed_recipient_id",
    signed_recipient_id: "signed_recipient_id",
}

// parseia "invoice:123 condo:5 erro" => { tokens: {invoice_id:"123", condo_id:"5"}, rest:"erro" }
const parseSearchTokens = (input) => {
    const text = String(input || "")
    const parts = text.split(/\s+/).filter(Boolean)

    const tokens = {}
    const restParts = []

    for (const part of parts) {
        const idx = part.indexOf(":")
        if (idx <= 0) {
            restParts.push(part)
            continue
        }

        const rawKey = part.slice(0, idx).trim().toLowerCase()
        const rawVal = part.slice(idx + 1).trim()

        const queryKey = TOKEN_TO_QUERY_PARAM[rawKey]
        if (!queryKey || !rawVal) {
            restParts.push(part)
            continue
        }

        tokens[queryKey] = rawVal
    }

    return { tokens, rest: restParts.join(" ").trim() }
}

export default function LogsViewer({
                                       title = "Logs",
                                       endpoint = "log/",
                                       pageSize = 500,
                                       searchParamName = "search",
                                       debounceMs = 1000,
                                   }) {
    const [searchParams, setSearchParams] = useSearchParams()

    const [loading, setLoading] = useState(false)
    const [logs, setLogs] = useState([])
    const [selectedId, setSelectedId] = useState(null)

    const [totalCount, setTotalCount] = useState(0)
    const [page, setPage] = useState(1)

    const [fileLoading, setFileLoading] = useState(false)
    const [fileJson, setFileJson] = useState(null)
    const [fileError, setFileError] = useState("")
    const [loadedFromUrl, setLoadedFromUrl] = useState("")

    // painel de filtros
    const [filtersOpen, setFiltersOpen] = useState(false)
    const [startDate, setStartDate] = useState(null)
    const [endDate, setEndDate] = useState(null)
    const [filterIdentifier, setFilterIdentifier] = useState("")
    const [filterInvoiceId, setFilterInvoiceId] = useState("")
    const [filterTransferId, setFilterTransferId] = useState("")
    const [filterPaymentId, setFilterPaymentId] = useState("")
    const [filterMethod, setFilterMethod] = useState("")
    const [filterIds, setFilterIds] = useState("")

    // input do usuário
    const [searchText, setSearchText] = useState("")

    // debounce nos campos de texto dos filtros
    const debouncedIdentifier = useDebouncedValue(filterIdentifier, debounceMs)
    const debouncedInvoiceId = useDebouncedValue(filterInvoiceId, debounceMs)
    const debouncedTransferId = useDebouncedValue(filterTransferId, debounceMs)
    const debouncedPaymentId = useDebouncedValue(filterPaymentId, debounceMs)
    const debouncedIds = useDebouncedValue(filterIds, debounceMs)

    // ✅ aplica debounce no texto todo (inclusive invoice: / condo:)
    const debouncedSearchText = useDebouncedValue(searchText, debounceMs)

    // ✅ parse após debounce
    const parsedAfterDebounce = useMemo(() => parseSearchTokens(debouncedSearchText), [debouncedSearchText])

    // ✅ filtros lidos da URL
    const urlFilters = useMemo(() => {
        const invoice_id = searchParams.get("invoice_id")
        const signed_recipient_id = searchParams.get("signed_recipient_id")
        const condo_id = searchParams.get("condo_id")
        const transfer_id = searchParams.get("transfer_id")
        const payment_id = searchParams.get("payment_id")

        return cleanParams({
            invoice_id,
            signed_recipient_id,
            condo_id,
            transfer_id,
            payment_id,
        })
    }, [searchParams])

    const urlFiltersEntries = useMemo(() => Object.entries(urlFilters || {}), [urlFilters])

    const removeUrlFilter = useCallback(
        (keyToRemove) => {
            const next = new URLSearchParams(searchParams)
            next.delete(keyToRemove)
            setSearchParams(next)
        },
        [searchParams, setSearchParams]
    )

    // ✅ aplica tokens no GET SOMENTE após debounce
    useEffect(() => {
        const tokens = parsedAfterDebounce.tokens
        const hasTokens = Object.keys(tokens).length > 0
        if (!hasTokens) return

        const next = new URLSearchParams(searchParams)
        let changed = false

        Object.entries(tokens).forEach(([k, v]) => {
            if (next.get(k) !== String(v)) {
                next.set(k, String(v))
                changed = true
            }
        })

        if (changed) setSearchParams(next)

        // ✅ agora sim: limpa o texto removendo tokens (só depois do debounce)
        // (mantém o que sobrou como busca normal)
        const rest = parsedAfterDebounce.rest
        if (rest !== debouncedSearchText) {
            // Não mexe no cursor enquanto digita, porque só roda após debounce.
            setSearchText(rest)
        }

    }, [parsedAfterDebounce.tokens, parsedAfterDebounce.rest]) // tokens/rest mudaram após debounce

    // ✅ texto final para o search= da API (após debounce, já sem tokens)
    const debouncedSearchForApi = useMemo(() => parsedAfterDebounce.rest, [parsedAfterDebounce.rest])

    const selectedLog = useMemo(
        () => logs.find((l) => String(l.id) === String(selectedId)) ?? null,
        [logs, selectedId]
    )

    const totalPages = useMemo(() => Math.max(1, Math.ceil(totalCount / pageSize)), [totalCount, pageSize])

    const loadLogs = useCallback(async () => {
        setLoading(true)
        try {
            const params = cleanParams({
                page_size: pageSize,
                page,
                ...urlFilters,
                [searchParamName]: debouncedSearchForApi,
                start_date: startDate ? moment(startDate).format("YYYY-MM-DD") : undefined,
                end_date: endDate ? moment(endDate).format("YYYY-MM-DD") : undefined,
                identifier: debouncedIdentifier,
                invoice_id: debouncedInvoiceId || urlFilters.invoice_id,
                transfer_id: debouncedTransferId || urlFilters.transfer_id,
                payment_id: debouncedPaymentId || urlFilters.payment_id,
                method: filterMethod,
                id: debouncedIds,
            })

            const { data } = await api.get(endpoint, { params })

            const countFromApi = Number.isFinite(Number(data?.count)) ? Number(data.count) : null
            setTotalCount(countFromApi ?? (data?.results?.length ?? (Array.isArray(data) ? data.length : 0)))

            const items = data?.results ?? data ?? []
            const normalized = Array.isArray(items) ? items : []
            setLogs(normalized)
            setSelectedId(normalized[0]?.id ?? null)
        } finally {
            setLoading(false)
        }
    }, [endpoint, pageSize, page, urlFilters, searchParamName, debouncedSearchForApi, startDate, endDate, debouncedIdentifier, debouncedInvoiceId, debouncedTransferId, debouncedPaymentId, filterMethod, debouncedIds])

    const loadJsonFromFileUrl = async (fileUrl) => {
        setFileLoading(true)
        setFileJson(null)
        setFileError("")
        setLoadedFromUrl(fileUrl || "")

        if (!fileUrl) {
            setFileError("Este log não possui URL no campo 'file'.")
            setFileLoading(false)
            return
        }

        try {
            const resp = await fetch(fileUrl)
            if (!resp.ok) throw new Error(`Falha ao buscar arquivo (HTTP ${resp.status}).`)

            const text = await resp.text()
            try {
                setFileJson(JSON.parse(text))
            } catch {
                setFileJson({ raw: text })
            }
        } catch (e) {
            setFileError("Não consegui carregar o JSON do S3. Possíveis causas: URL expirada, permissão, ou CORS no bucket.")
        } finally {
            setFileLoading(false)
        }
    }

    // volta pra página 1 quando filtros mudam
    useEffect(() => {
        setPage(1)
    }, [debouncedSearchForApi, urlFilters, startDate, endDate, debouncedIdentifier, debouncedInvoiceId, debouncedTransferId, debouncedPaymentId, filterMethod, debouncedIds])

    useEffect(() => {
        loadLogs()
    }, [loadLogs])

    useEffect(() => {
        if (selectedLog?.file) loadJsonFromFileUrl(selectedLog.file)
        else {
            setFileJson(null)
            setFileError("")
            setLoadedFromUrl("")
        }
    }, [selectedLog?.id])

    const activeFilterCount = useMemo(() => {
        let count = 0
        if (startDate) count++
        if (endDate) count++
        if (filterIdentifier.trim()) count++
        if (filterInvoiceId.trim()) count++
        if (filterTransferId.trim()) count++
        if (filterPaymentId.trim()) count++
        if (filterMethod) count++
        if (filterIds.trim()) count++
        return count
    }, [startDate, endDate, filterIdentifier, filterInvoiceId, filterTransferId, filterPaymentId, filterMethod, filterIds])

    const clearAllFilters = useCallback(() => {
        setSearchText("")
        setStartDate(null)
        setEndDate(null)
        setFilterIdentifier("")
        setFilterInvoiceId("")
        setFilterTransferId("")
        setFilterPaymentId("")
        setFilterMethod("")
        setFilterIds("")
        setPage(1)
        // limpa URL params
        const next = new URLSearchParams()
        setSearchParams(next)
    }, [setSearchParams])

    return (
        <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="pt-br">
        <Card sx={{ borderRadius: 3 }}>
            <CardHeader
                avatar={<TerminalOutlined />}
                title={<Typography variant="h6" fontWeight={900}>{title}</Typography>}
                action={
                    <Tooltip title="Atualizar">
            <span>
              <IconButton onClick={loadLogs} disabled={loading}>
                <Refresh />
              </IconButton>
            </span>
                    </Tooltip>
                }
            />
            <Divider />

            <Grid container sx={{ height: "calc(100vh - 140px)", minHeight: 520 }}>
                {/* LISTA */}
                <Grid
                    item
                    xs={12}
                    md={4}
                    sx={{
                        borderRight: { md: "1px solid", xs: "none" },
                        borderColor: "divider",
                        height: "100%",
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <Box sx={{ p: 1.5, pb: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <TextField
                                size="small"
                                fullWidth
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                placeholder="Buscar..."
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    ),
                                    endAdornment: searchText ? (
                                        <InputAdornment position="end">
                                            <Tooltip title="Limpar busca">
                                                <IconButton size="small" onClick={() => setSearchText("")}>
                                                    <ClearIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </InputAdornment>
                                    ) : null,
                                }}
                            />
                            <Tooltip title={filtersOpen ? "Ocultar filtros" : "Mostrar filtros"}>
                                <IconButton
                                    size="small"
                                    onClick={() => setFiltersOpen((v) => !v)}
                                    color={activeFilterCount > 0 ? "primary" : "default"}
                                >
                                    <FilterListIcon fontSize="small" />
                                    {activeFilterCount > 0 && (
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                position: "absolute",
                                                top: -2,
                                                right: -2,
                                                bgcolor: "primary.main",
                                                color: "primary.contrastText",
                                                borderRadius: "50%",
                                                width: 16,
                                                height: 16,
                                                fontSize: 10,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}
                                        >
                                            {activeFilterCount}
                                        </Typography>
                                    )}
                                </IconButton>
                            </Tooltip>
                        </Stack>

                        <Collapse in={filtersOpen}>
                            <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                                <Stack direction="row" spacing={1}>
                                    <DatePicker
                                        label="Data inicial"
                                        value={startDate}
                                        onChange={(v) => setStartDate(v)}
                                        format="DD/MM/YYYY"
                                        slotProps={{ textField: { size: "small", fullWidth: true } }}
                                    />
                                    <DatePicker
                                        label="Data final"
                                        value={endDate}
                                        onChange={(v) => setEndDate(v)}
                                        format="DD/MM/YYYY"
                                        minDate={startDate || undefined}
                                        slotProps={{ textField: { size: "small", fullWidth: true } }}
                                    />
                                </Stack>

                                <TextField
                                    size="small"
                                    fullWidth
                                    label="Método"
                                    select
                                    value={filterMethod}
                                    onChange={(e) => setFilterMethod(e.target.value)}
                                >
                                    <MenuItem value="">
                                        <em>Todos</em>
                                    </MenuItem>
                                    {METHOD_OPTIONS.map((m) => (
                                        <MenuItem key={m} value={m}>{m}</MenuItem>
                                    ))}
                                </TextField>

                                <TextField
                                    size="small"
                                    fullWidth
                                    label="Identificador"
                                    placeholder="CPF, tx_id ou barcode"
                                    value={filterIdentifier}
                                    onChange={(e) => setFilterIdentifier(e.target.value)}
                                />

                                <Stack direction="row" spacing={1}>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        label="Fatura (invoice_id)"
                                        value={filterInvoiceId}
                                        onChange={(e) => setFilterInvoiceId(e.target.value)}
                                    />
                                    <TextField
                                        size="small"
                                        fullWidth
                                        label="Repasse (transfer_id)"
                                        value={filterTransferId}
                                        onChange={(e) => setFilterTransferId(e.target.value)}
                                    />
                                </Stack>

                                <TextField
                                    size="small"
                                    fullWidth
                                    label="Pagamento (payment_id)"
                                    value={filterPaymentId}
                                    onChange={(e) => setFilterPaymentId(e.target.value)}
                                />

                                <TextField
                                    size="small"
                                    fullWidth
                                    label="IDs (separados por vírgula)"
                                    placeholder="23,56,4"
                                    value={filterIds}
                                    onChange={(e) => setFilterIds(e.target.value)}
                                />

                                {activeFilterCount > 0 && (
                                    <Button
                                        size="small"
                                        variant="text"
                                        color="error"
                                        startIcon={<ClearIcon fontSize="small" />}
                                        onClick={clearAllFilters}
                                        sx={{ textTransform: "none", alignSelf: "flex-start" }}
                                    >
                                        Limpar todos os filtros
                                    </Button>
                                )}
                            </Stack>
                        </Collapse>

                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 1, flexWrap: "wrap", gap: 0.5 }}>
                            <Chip size="small" variant="outlined" label={`${totalCount} resultados`} />
                            {debouncedSearchForApi ? <Chip size="small" variant="outlined" label={`Busca: "${debouncedSearchForApi}"`} /> : null}

                            {urlFiltersEntries.map(([key, value]) => (
                                <Chip
                                    key={`${key}-${value}`}
                                    size="small"
                                    variant="outlined"
                                    label={`${FILTER_LABELS[key] || key}: ${value}`}
                                    onDelete={() => removeUrlFilter(key)}
                                    deleteIcon={<CancelIcon sx={{ fontSize: 18, opacity: 0.7, "&:hover": { opacity: 1 } }} />}
                                />
                            ))}
                        </Stack>
                    </Box>

                    <Divider />

                    <Box sx={{ p: 1.5, pt: 1.5, flex: 1, minHeight: 0, overflowY: "auto" }}>
                        {loading ? (
                            <Stack spacing={1}>
                                {Array.from({ length: 12 }).map((_, i) => (
                                    <Skeleton key={i} height={44} />
                                ))}
                            </Stack>
                        ) : (
                            <List dense disablePadding sx={{ "& .MuiListItemButton-root": { borderRadius: 2 } }}>
                                {logs.map((l) => {
                                    const isSelected = String(l.id) === String(selectedId)
                                    return (
                                        <ListItemButton
                                            key={l.id}
                                            selected={isSelected}
                                            onClick={() => setSelectedId(l.id)}
                                            sx={{
                                                border: isSelected ? "1px solid" : "1px solid transparent",
                                                borderColor: isSelected ? "primary.main" : "transparent",
                                                bgcolor: isSelected ? "action.hover" : "transparent",
                                                px: 1,
                                                py: 0.75,
                                                mb: 0.75,
                                            }}
                                        >
                                            <Stack direction="row" spacing={1} alignItems="center" sx={{ width: "100%" }}>
                                                <Chip
                                                    size="small"
                                                    color={methodColor(l.method)}
                                                    label={(l.method || "-").toString().toUpperCase()}
                                                    sx={{ minWidth: 96 }}
                                                />

                                                <ListItemText
                                                    primary={
                                                        <Typography variant="body2" fontWeight={900} noWrap>
                                                            {l.identifier || "-"}
                                                        </Typography>
                                                    }
                                                    secondary={
                                                        <Typography variant="caption" color="text.secondary" noWrap>
                                                            {l.type || "-"} • {formatDate(l.created_at)} {formatTime(l.created_at)}
                                                        </Typography>
                                                    }
                                                    sx={{ my: 0 }}
                                                />

                                                <Tooltip title={l.success ? "Sucesso" : "Falha"}>
                                                    <Chip
                                                        size="small"
                                                        color={l.success ? "success" : "error"}
                                                        icon={l.success ? <CheckCircleOutline fontSize="small" /> : <ErrorOutline fontSize="small" />}
                                                        label={l.success ? "OK" : "ERRO"}
                                                        variant="outlined"
                                                    />
                                                </Tooltip>
                                            </Stack>
                                        </ListItemButton>
                                    )
                                })}

                                {!logs.length && (
                                    <Box sx={{ p: 2 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            Nenhum log encontrado.
                                        </Typography>
                                    </Box>
                                )}
                            </List>
                        )}
                    </Box>

                    {totalPages > 1 && (
                        <>
                            <Divider />
                            <Stack direction="row" justifyContent="center" alignItems="center" spacing={1} sx={{ p: 1 }}>
                                <IconButton size="small" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                                    <NavigateBefore fontSize="small" />
                                </IconButton>
                                <Typography variant="caption">
                                    {page} / {totalPages}
                                </Typography>
                                <IconButton size="small" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                                    <NavigateNext fontSize="small" />
                                </IconButton>
                            </Stack>
                        </>
                    )}
                </Grid>

                {/* DETALHE + JSON */}
                <Grid
                    item
                    xs={12}
                    md={8}
                    sx={{
                        height: "100%",
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <Box sx={{ p: 2, flex: 1, minHeight: 0, overflowY: "auto" }}>
                        {!selectedLog ? (
                            <Typography variant="body2" color="text.secondary">
                                Selecione um log à esquerda.
                            </Typography>
                        ) : (
                            <Stack spacing={2}>
                                <Card variant="outlined" sx={{ borderRadius: 3 }}>
                                    <CardContent>
                                        <Stack spacing={1}>
                                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                                <Stack spacing={0.25}>
                                                    <Typography variant="subtitle1" fontWeight={900}>
                                                        {selectedLog.identifier || "-"}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {selectedLog.type || "-"} • {formatDate(selectedLog.created_at)} {formatTime(selectedLog.created_at)}
                                                    </Typography>
                                                </Stack>

                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Chip size="small" color={methodColor(selectedLog.method)} label={(selectedLog.method || "-").toUpperCase()} />
                                                    <Chip
                                                        size="small"
                                                        color={selectedLog.success ? "success" : "error"}
                                                        variant="outlined"
                                                        label={selectedLog.success ? "Sucesso" : "Falha"}
                                                    />
                                                </Stack>
                                            </Stack>

                                            <Divider />

                                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                                <Chip
                                                    size="small"
                                                    variant="outlined"
                                                    icon={<DescriptionOutlined fontSize="small" />}
                                                    label={selectedLog.file ? "Arquivo (S3)" : "Sem arquivo"}
                                                />

                                                {selectedLog.file ? (
                                                    <>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            startIcon={<OpenInNew fontSize="small" />}
                                                            sx={{ textTransform: "none" }}
                                                            component={Link}
                                                            href={selectedLog.file}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                        >
                                                            Abrir arquivo
                                                        </Button>

                                                        <Button
                                                            size="small"
                                                            variant="text"
                                                            startIcon={<ContentCopy fontSize="small" />}
                                                            sx={{ textTransform: "none" }}
                                                            onClick={() => copyText(selectedLog.file)}
                                                        >
                                                            Copiar URL
                                                        </Button>

                                                        <Button
                                                            size="small"
                                                            variant="text"
                                                            sx={{ textTransform: "none" }}
                                                            onClick={() => loadJsonFromFileUrl(selectedLog.file)}
                                                            disabled={fileLoading}
                                                        >
                                                            Recarregar JSON
                                                        </Button>
                                                    </>
                                                ) : null}
                                            </Stack>
                                        </Stack>
                                    </CardContent>
                                </Card>

                                <Card variant="outlined" sx={{ borderRadius: 3 }}>
                                    <CardHeader
                                        title={<Typography variant="subtitle2" fontWeight={900}>JSON (carregado do campo file)</Typography>}
                                        action={
                                            <Tooltip title="Copiar JSON">
                        <span>
                          <IconButton disabled={!fileJson} onClick={() => copyText(safeStringify(fileJson))}>
                            <ContentCopy fontSize="small" />
                          </IconButton>
                        </span>
                                            </Tooltip>
                                        }
                                        sx={{ py: 1.2 }}
                                    />
                                    <Divider />
                                    <CardContent>
                                        {fileLoading ? (
                                            <Stack spacing={1}>
                                                <Skeleton height={22} />
                                                <Skeleton height={22} />
                                                <Skeleton height={22} />
                                                <Skeleton height={22} />
                                            </Stack>
                                        ) : fileError ? (
                                            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: "action.hover", border: "1px solid", borderColor: "divider" }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    {fileError}
                                                </Typography>

                                                {loadedFromUrl ? (
                                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, flexWrap: "wrap" }}>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            startIcon={<OpenInNew fontSize="small" />}
                                                            sx={{ textTransform: "none" }}
                                                            component={Link}
                                                            href={loadedFromUrl}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                        >
                                                            Abrir mesmo assim
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            variant="text"
                                                            startIcon={<ContentCopy fontSize="small" />}
                                                            sx={{ textTransform: "none" }}
                                                            onClick={() => copyText(loadedFromUrl)}
                                                        >
                                                            Copiar URL
                                                        </Button>
                                                    </Stack>
                                                ) : null}
                                            </Box>
                                        ) : (
                                            <Box
                                                sx={{
                                                    bgcolor: "action.hover",
                                                    borderRadius: 2,
                                                    border: "1px solid",
                                                    borderColor: "divider",
                                                    p: 1.5,
                                                    overflow: "auto",
                                                    maxHeight: 460,
                                                }}
                                            >
                                                <Typography
                                                    component="pre"
                                                    variant="body2"
                                                    sx={{
                                                        m: 0,
                                                        fontSize: 12,
                                                        lineHeight: 1.45,
                                                        fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                                                        whiteSpace: "pre",
                                                    }}
                                                >
                                                    {safeStringify(fileJson ?? {})}
                                                </Typography>
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            </Stack>
                        )}
                    </Box>
                </Grid>
            </Grid>
        </Card>
        </LocalizationProvider>
    )
}
